<template>
  <div class="wt2-producing-content">
    <video
      ref="video"
      class="-producing-video"
      loop
      autoplay
      :poster="posterPath"
      :src="videoPath"
    />

    <div v-show="getActionPanelOpenStatus">
      <wt-base-panel :on-hide="hideActionPanel" type="overlay">
        <producing-panel
          :video="video"
          :is-video-failed="isVideoFailed"
          @openMakeCopy="openMakeCopy"
        />
      </wt-base-panel>
    </div>

    <div class="-wt2-producing-content-actions">
      <div v-if="isVideoFailed" class="wt2-color-pink-l -title wt2-text-3xl">
        <span>Producing Failed</span>
      </div>
      <div v-else class="wt2-color-negative -title wt2-text-3xl">
        <span v-if="!isVideoTemplate">{{
          'producing.subTitleHeader' | culture
        }}</span>
        <span v-if="!isVideoTemplate" class="-dots-animate" v-animate-sequence
          >...</span
        >
        <div v-else>
          <span>{{ 'producing.subTitleHeaderStoryTemplate' | culture }}</span>
        </div>
      </div>
      <div v-if="isVideoFailed" class="-text-box">
        <span class="wt2-text-m wt2-color-negative"
          >Ouch… looks like a problem!</span
        >
        <span class="wt2-text-m wt2-color-negative">
          Your video failed to produce, but no worry, our support team is on it.
          <br />For more details contact us on the chat below.
        </span>
      </div>
      <div v-else class="-text-box">
        <span
          v-if="isLongVideo"
          class="wt2-text-l wt2-color-negative"
          v-text="longVideoTitle"
        />
        <span v-if="!isVideoTemplate" class="wt2-text-l wt2-color-negative">{{
          'producing.subTitleHeaderDesc' | culture
        }}</span>
        <span v-if="isVideoTemplate" class="wt2-text-l wt2-color-negative">{{
          'producing.subTitleHeaderStoryTemplateDesc' | culture
        }}</span>
      </div>

      <div v-if="!isVideoFailed" class="-actions-buttons">
        <wt-button
          v-if="isVideoTemplate"
          type="secondary"
          :text="'generic.MAKE_COPY' | culture"
          @click="openMakeCopy(true)"
        />
        <wt-button
          v-else
          type="secondary"
          :disabled="!isUserCanCreateStories"
          :text="'generic.MAKE_COPY' | culture"
          @click="openMakeCopy(false)"
        />

        <wt-button
          type="primary"
          :disabled="!isUserCanCreateStories"
          :text="makeNewWochitTitle"
          @click="openMakeNewWochit"
        />
      </div>
      <template v-if="!isVideoFailed">
        <wt-button
          v-if="isVideoTemplate"
          class="-custom -hover-blue -remove-hover-background wt2-color-negative -home"
          type="transparent"
          :text="'generic.DISCOVER_MORE_TEMPLATES' | culture"
          @click="goToDiscovered"
        />
        <wt-button
          class="-custom -hover-blue -remove-hover-background wt2-color-negative -home"
          type="transparent"
          :text="'generic.GO_TO_HOME' | culture"
          @click="goToHome"
        />
      </template>
      <template v-else>
        <wt-button
          v-if="isVideoTemplate"
          class="-custom -remove-hover-background wt2-color-negative -home"
          type="hollow-light"
          :text="'generic.DISCOVER_MORE_TEMPLATES' | culture"
          @click="goToDiscovered"
        />
        <wt-button
          class="-custom -remove-hover-background wt2-color-negative -home"
          type="hollow-light"
          :text="'generic.GO_TO_HOME' | culture"
          @click="goToHome"
        />
      </template>
    </div>
  </div>
</template>

<script>
import app from 'app';
import ProducingPanel from 'v/Producing/ProducingPanel.vue';
import ProducingActionsMixin from 'v/Producing/ProducingActionsMixin.vue';
import { mapGetters, mapMutations } from 'vuex';
import { DuplicateController } from 'c/DuplicateController';

export default {
  mixins: [ProducingActionsMixin],

  props: {
    video: { type: Object, required: true },
    isVideoFailed: { type: Boolean, default: false },
  },

  components: {
    ProducingPanel,
  },

  beforeMount() {
    this.hideActionPanel();
  },

  mounted() {
    if (this.isVideoFailed) {
      this.stopVideo();
    }
  },

  watch: {
    isVideoFailed(isFailed) {
      if (isFailed) {
        this.stopVideo();
      }
    },
  },

  computed: {
    ...mapGetters(['getActionPanelOpenStatus']),

    storySoftMaxDuration() {
      const programmer = app.programmers.get(this.video.programmerId);

      return programmer
        ? app.sfp(programmer.get('storySoftMaxDuration'), 1e3)
        : false;
    },

    videoPath() {
      return app.DEFAULT.producingVideo;
    },

    posterPath() {
      return 'https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/producing_poster.png';
    },

    isLongVideo() {
      return this.storySoftMaxDuration
        ? this.video.videoDurationSec >= this.storySoftMaxDuration
        : false;
    },

    longVideoTitle() {
      return app.culture.producing.subTitleLongVideo.format(
        app.moment.duration(this.storySoftMaxDuration, 'seconds').humanize()
      );
    },
  },

  methods: {
    ...mapMutations(['hideActionPanel', 'showActionPanel']),

    stopVideo() {
      this.$refs.video && this.$refs.video.pause && this.$refs.video.pause();
    },

    openMakeCopy(keepAsStoryboard) {
      DuplicateController.openDialog({
        story: this.video,
        message: app.culture.makeCopyDialog.messageVideo,
        keepAsStoryboard,
        storyStartPoint: app.storyStartPoint.DUPLICATE_IN_PROGRESS_STORY,
        source: 'producing_page',
      }).then((data) => {
        this.$emit('stopListening');

        if (data.changeOrg) {
          app.delayedSpinner.hide();
        } else {
          app.state.enableNavigation();
          app.navigate(`stories/${data.storyId}`);
        }
      });
    },

    goToDiscovered() {
      app.state.enableNavigation();
      app.navigate('dashboard/storyboards');
    },
  },
};
</script>
